import React, { useState } from 'react'
import ReportLayout from '../../layouts/Report'

import useGetSchools from '../../../hooks/schools/useGetSchools'
import useGetCampuses from '../../../hooks/campuses/useGetCampuses'
import useGetInterviewReport from '../../../hooks/analytics/useGetInterviewReport'
import useExportInterviewReport from '../../../hooks/analytics/useExportInterviewReport'

import ReportTablet from '../../sub_components/ReportTablet'
import ReportDatePicker from '../../sub_components/ReportDatePicker'
import FilterTable from '../../sub_components/FilterTable'
import BarChart from '../../sub_components/charts/BarChart'

import { getDateString } from '../../../helpers/utility'

import { InterviewQuestionType, InterviewMode } from '../../../interfaces/types'
import { IColumnProps, IFilterableDataProps } from '../../../interfaces/table'


const InterviewReport = () => {
    const [schoolFilters, setSchoolFilters] = useState<number[]>([]);
    const [campusFilters, setCampusFilters] = useState<number[]>([]);
    const [questionTypeFilters, setQuestionTypeFilters] = useState<number[]>([]);
    const [modeFilters, setModeFilters] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const interviewQuestionTypeOptions = Object.values(InterviewQuestionType).map((type, index) => ({
        id: index,
        name: type,
    }));
    const interviewModeOptions = Object.values(InterviewMode).map((mode, index) => ({
        id: index,
        name: mode,
    }));

    const filterQuery = new URLSearchParams();
    filterQuery.set("start_date", startDate);
    filterQuery.set("end_date", endDate);
    schoolFilters.forEach((id) => filterQuery.append("school_ids", id.toString()));
    campusFilters.forEach((id) => filterQuery.append("campus_ids", id.toString()));
    questionTypeFilters.forEach((id) => filterQuery.append("question_types", interviewQuestionTypeOptions[id].name));
    modeFilters.forEach((id) => filterQuery.append("modes", interviewModeOptions[id].name));

    const { data: schools } = useGetSchools();
    const { data: campuses } = useGetCampuses();
    const { data: interviewReport } = useGetInterviewReport(filterQuery.toString());

    const { mutate: exportCsv } = useExportInterviewReport();

    let filterTableDataColumns__Schools: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "School Name", identifier: "name" },
    ];
    let filterTableDataColumns__Campuses: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Campus Name", identifier: "name" },
    ];
    let filterTableDataColumns__QuestionType: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Interview Type", identifier: "name" },
    ];
    let filterTableDataColumns__Mode: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Interview Mode", identifier: "name" },
    ];
    let filterTableDataColumns__InterviewReport: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Full Name", identifier: "full_name" },
        { name: "Email", identifier: "email" },
        { name: "Type", identifier: "question_type" },
        { name: "Mode", identifier: "mode" },
        { name: "Completed At", identifier: "completed_at" },
    ];

    let filterTableData__Schools: IFilterableDataProps[] = [];
    let filterTableData__Campuses: IFilterableDataProps[] = [];
    let filterTableData__QuestionType: IFilterableDataProps[] = [];
    let filterTableData__Mode: IFilterableDataProps[] = [];
    let filterTableData__InterviewReport: IFilterableDataProps[] = [];

    if (schools) {
        filterTableData__Schools = schools.map((school) => ({
            id: school.id,
            name: school.name,
        }));
    }

    if (campuses) {
        filterTableData__Campuses = campuses.map((campus) => ({
            id: campus.id,
            name: campus.name,
        }));
    }

    if (interviewQuestionTypeOptions) {
        filterTableData__QuestionType = interviewQuestionTypeOptions.map((type) => ({
            id: type.id,
            name: type.name.charAt(0).toUpperCase() + type.name.slice(1),
        }));
    }

    if (interviewModeOptions) {
        filterTableData__Mode = interviewModeOptions.map((mode) => ({
            id: mode.id,
            name: mode.name.charAt(0).toUpperCase() + mode.name.slice(1),
        }));
    }

    if (interviewReport) {
        filterTableData__InterviewReport = interviewReport.table_data.map((interview) => ({
            id: interview.id,
            full_name: interview.full_name,
            email: interview.email,
            question_type: interview.question_type,
            mode: interview.mode,
            completed_at: getDateString(interview.completed_at),
        }));
    }

    const onExportCsvReport = () => {
        exportCsv({
            payload: {
                filterQuery: filterQuery.toString(),
            }
        });
    }

    return (
        <ReportLayout>
            <div className='immersion_admin__careers_report'>
                <div className='immersion_admin__careers_report__header'>
                    <div className='immersion_admin__careers_report__header__content'>
                        <h2 className='immersion_admin__careers_report__header__content__title'>Interview Report</h2>
                        <p className='immersion_admin__careers_report__header__content__description'>
                            This slide gives an overview of interviews completed by students.
                        </p>
                    </div>
                    <div className='immersion_admin__careers_report__header__actions'>
                        <button className='immersion_admin__btn__primary immersion_admin__careers_report__header__actions__button' onClick={onExportCsvReport}>Export CSV</button>
                    </div>
                </div>

                <div className='immersion_admin__careers_report__chart_body'>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__Schools}
                            data={filterTableData__Schools}
                            enableFilters={true}
                            filters={schoolFilters}
                            setFilters={setSchoolFilters}
                        />
                        <FilterTable
                            columns={filterTableDataColumns__Campuses}
                            data={filterTableData__Campuses}
                            enableFilters={true}
                            filters={campusFilters}
                            setFilters={setCampusFilters}
                        />

                        <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__vertical'>
                            <ReportDatePicker
                                required={true}
                                startDate={startDate}
                                setStartDate={setStartDate}
                                endDate={endDate}
                                setEndDate={setEndDate}
                            />
                            <div className='immersion_admin__careers_report__chart__row'>
                                <ReportTablet
                                    title={interviewReport?.total_interviews_completed?.toString() ?? "0"}
                                    description='Total Interviews Completed'
                                    color={'green'}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__QuestionType}
                            data={filterTableData__QuestionType}
                            enableFilters={true}
                            filters={questionTypeFilters}
                            setFilters={setQuestionTypeFilters}
                        />
                        {interviewReport?.question_type_chart_data.length ? (
                            <BarChart
                                width={600}
                                height={318}
                                data={interviewReport?.question_type_chart_data ?? []}
                                xLabel="Interview Type"
                                yLabel="No. of Interviews"
                                xInterval={0}
                            />
                        ) : (
                            <div className='immersion_admin__careers_report__chart__no_data'>
                                No interviews completed
                            </div>
                        )}
                    </div>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__Mode}
                            data={filterTableData__Mode}
                            enableFilters={true}
                            filters={modeFilters}
                            setFilters={setModeFilters}
                        />
                        {interviewReport?.mode_chart_data.length ? (
                            <BarChart
                                width={600}
                                height={318}
                                data={interviewReport?.mode_chart_data ?? []}
                                xLabel="Mode"
                                yLabel="No. of Interviews"
                                xInterval={0}
                            />
                        ) : (
                            <div className='immersion_admin__careers_report__chart__no_data'>
                                No interviews completed
                            </div>
                        )}
                    </div>
                    <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__width_75'>
                        <FilterTable
                            columns={filterTableDataColumns__InterviewReport}
                            data={filterTableData__InterviewReport}
                            enableFilters={false}
                            filters={[]}
                            setFilters={() => { }}
                        />
                    </div>
                </div>
            </div>
        </ReportLayout>
    )
}

export default InterviewReport;